<script lang="ts">
	import type { IconName } from '$lib/components/icons/types';

	import { createSlider } from '@melt-ui/svelte';
	import { debounce } from 'throttle-debounce';
	import PriceRange from '../priceRange/PriceRange.svelte';

	interface Props {
		min?: number;
		max?: number;
		defaultValue: number[];
		step?: number;
		label: string;
		getValueLabel: (value: number[], max: number) => string;
		onValueChange?: (value: number[]) => void;
		labelIcon?: IconName;
	}

	const {
		min = 0,
		max = 100,
		defaultValue,
		step = 1,
		label,
		getValueLabel,
		onValueChange = () => {},
		labelIcon = undefined,
	}: Props = $props();

	const debouncedDispatchChange = debounce(300, onValueChange);

	const {
		elements: { root, range, thumbs },
		states: { value: innerValue },
		options: { min: innerMin, max: innerMax },
	} = createSlider({
		defaultValue,
		min,
		max,
		step,
		onValueChange({ next, curr }) {
			if (next[0] !== curr[0] || next[1] !== curr[1]) {
				debouncedDispatchChange(next);
			}
			return next;
		},
	});

	$effect.pre(() => {
		innerMin.set(min);
	});
	$effect.pre(() => {
		innerMax.set(max);
	});

	const leftSpanClass = $derived(defaultValue.length === 1 ? 'h-1 bg-coloredneutral' : 'h-0.25 bg-coloredneutral-weakest');
</script>

<div>
	<div class="flex justify-between items-center text-coloredneutral pb-3 text-sm">
		<div>{label}</div>
		<div class="text-bold">
			{#if labelIcon}
				<PriceRange icon={labelIcon} length={max} filledLength={$innerValue[0]} />
			{:else}
				{getValueLabel($innerValue, max)}
			{/if}
		</div>
	</div>
	<div class="flex items-center">
		<span class="block w-3.5 {leftSpanClass} rd-l-full"></span>
		<!-- eslint-disable-next-line svelte/require-store-reactive-access -->
		<span {...$root} use:root class="relative flex h-2 w-full items-center range">
			<span class="block h-0.25 bg-coloredneutral-weakest w-full">
				<!-- eslint-disable-next-line svelte/require-store-reactive-access -->
				<span {...$range} use:range class="h-1 bg-coloredneutral top-3"></span>
			</span>
			<!-- eslint-disable-next-line svelte/require-each-key -->
			{#each $thumbs as thumb}
				<span
					{...thumb}
					use:thumb.action
					class="block h-7 w-7 rd-full bg-white shadow-material-elevation-3 border-solid border-0.5 border-neutral outline-unset z-1"
				></span>
			{/each}
		</span>
		<span class="block w-3.5 h-0.25 bg-coloredneutral-weakest"></span>
	</div>
</div>

<style>
	.range {
		width: calc(100% - 28px);
		padding: 14px 0;
	}
</style>
